const ARTICLE_TOC_BREAKPOINT = window.matchMedia('(min-width: 1068px)');

function getBeyondinkTocTargets() {
  const tocRoot = document.querySelector('.blog-toc[data-blog-toc]');
  const desktopHost = document.querySelector('[data-article-toc-desktop]');
  const mobileHost = document.querySelector('[data-article-toc-mobile]');
  const articleBody = document.querySelector('.post-content[itemprop="articleBody"]');

  if (!tocRoot || !desktopHost || !mobileHost || !articleBody) {
    return null;
  }

  return {
    tocRoot,
    tocBlock: tocRoot.closest('.shopify-block') || tocRoot,
    sourceRow: tocRoot.closest('.row'),
    desktopHost,
    mobileHost,
    articleBody,
  };
}

function slugifyBeyondinkHeading(value) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '') || 'section';
}

function ensureBeyondinkHeadingIds(headings) {
  const usedIds = new Set(
    Array.from(document.querySelectorAll('[id]'))
      .map((element) => element.id)
      .filter(Boolean)
  );

  headings.forEach((heading) => {
    if (heading.id) {
      usedIds.add(heading.id);
      return;
    }

    const baseId = slugifyBeyondinkHeading(heading.textContent.trim());
    let nextId = baseId;
    let index = 2;

    while (usedIds.has(nextId)) {
      nextId = `${baseId}-${index}`;
      index += 1;
    }

    heading.id = nextId;
    usedIds.add(nextId);
  });
}

function buildBeyondinkToc(tocRoot, articleBody) {
  const list = tocRoot.querySelector('[data-blog-toc-list]');

  if (!list) {
    return [];
  }

  const selectors = tocRoot.dataset.headingLevels || 'h2,h3';
  const headings = Array.from(articleBody.querySelectorAll(selectors))
    .filter((heading) => heading.textContent.trim().length > 0);

  if (!headings.length) {
    list.innerHTML = '';
    return [];
  }

  ensureBeyondinkHeadingIds(headings);

  list.innerHTML = headings.map((heading) => {
    const level = heading.tagName.replace('H', '');
    return `
      <li data-level="${level}">
        <a href="#${heading.id}" data-toc-target="${heading.id}">${heading.textContent.trim()}</a>
      </li>
    `;
  }).join('');

  return headings;
}

function syncBeyondinkTocLinks(tocRoot, headings) {
  const tocLinks = Array.from(tocRoot.querySelectorAll('.blog-toc__list a'));
  const headingMap = new Map(headings.map((heading) => [heading.id, heading]));

  tocLinks.forEach((link) => {
    const href = link.getAttribute('href') || '';
    const targetId = href.startsWith('#') ? href.slice(1) : '';
    const heading = headingMap.get(targetId);

    if (!targetId || !heading) {
      return;
    }

    link.dataset.tocTarget = targetId;

    const listItem = link.closest('li');
    if (listItem) {
      listItem.dataset.level = heading.tagName.replace('H', '');
    }
  });
}

function moveBeyondinkToc({ tocBlock, sourceRow, desktopHost, mobileHost }) {
  const nextHost = ARTICLE_TOC_BREAKPOINT.matches ? desktopHost : mobileHost;

  if (!nextHost || nextHost.contains(tocBlock)) {
    return;
  }

  nextHost.hidden = false;
  nextHost.appendChild(tocBlock);

  if (sourceRow) {
    sourceRow.classList.add('article-toc-source--relocated');
  }
}

function watchBeyondinkBreakpoint(callback) {
  if (typeof ARTICLE_TOC_BREAKPOINT.addEventListener === 'function') {
    ARTICLE_TOC_BREAKPOINT.addEventListener('change', callback);
    return;
  }

  if (typeof ARTICLE_TOC_BREAKPOINT.addListener === 'function') {
    ARTICLE_TOC_BREAKPOINT.addListener(callback);
  }
}

function initBeyondinkTocScrollSpy(tocRoot, headings) {
  if (!headings.length) {
    return;
  }

  const getTocLinks = () => Array.from(tocRoot.querySelectorAll('[data-toc-target]'));

  const setActiveLink = (activeId) => {
    const tocLinks = getTocLinks();

    tocLinks.forEach((link) => {
      link.classList.toggle('is-active', link.dataset.tocTarget === activeId);
    });
  };

  const getActivationLine = () => Math.max(120, window.innerHeight * 0.22);

  const updateActiveHeading = () => {
    const activationLine = getActivationLine();
    let activeHeading = headings[0];

    headings.forEach((heading) => {
      if (heading.getBoundingClientRect().top <= activationLine) {
        activeHeading = heading;
      }
    });

    setActiveLink(activeHeading.id);
  };

  const observer = new IntersectionObserver(() => {
    updateActiveHeading();
  }, {
    rootMargin: `-${getActivationLine()}px 0px -55% 0px`,
    threshold: [0, 0.2, 0.5, 1],
  });

  headings.forEach((heading) => observer.observe(heading));

  tocRoot.addEventListener('click', (event) => {
    const link = event.target.closest('[data-toc-target]');

    if (!link) {
      return;
    }

    setActiveLink(link.dataset.tocTarget);
  });

  const list = tocRoot.querySelector('[data-blog-toc-list]');
  if (list) {
    const mutationObserver = new MutationObserver(() => {
      syncBeyondinkTocLinks(tocRoot, headings);
      updateActiveHeading();
    });

    mutationObserver.observe(list, { childList: true, subtree: true });
  }

  updateActiveHeading();
  window.addEventListener('scroll', updateActiveHeading, { passive: true });
  window.addEventListener('resize', updateActiveHeading, { passive: true });
}

function initBeyondinkArticleToc() {
  const targets = getBeyondinkTocTargets();

  if (!targets || targets.tocRoot.dataset.articleTocReady === 'true') {
    return;
  }

  const headings = buildBeyondinkToc(targets.tocRoot, targets.articleBody);

  if (!headings.length) {
    return;
  }

  syncBeyondinkTocLinks(targets.tocRoot, headings);
  moveBeyondinkToc(targets);
  initBeyondinkTocScrollSpy(targets.tocRoot, headings);
  watchBeyondinkBreakpoint(() => moveBeyondinkToc(targets));
  targets.tocRoot.dataset.articleTocReady = 'true';
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBeyondinkArticleToc, { once: true });
} else {
  initBeyondinkArticleToc();
}
